<script setup>
import { ref, computed, onMounted, onUnmounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import { connectTatami, sendTatami, closeTatami } from "@/utils/ws_peticiones_evento";

import TatamiHeader from "./TatamiHeader.vue";
import DeclaraGanador from "./DeclaraGanador.vue";
import ProximasPeleas from "./ProximasPeleas.vue";

const route = useRoute();
const router = useRouter();

const wsConectado = ref(false);
const rawId = route.params.tatamiId || route.params.id;
const tatamiId = ref(rawId && rawId !== "null" ? Number(rawId) : null);

const peleas = ref([]);
const peleaActualId = ref(null);
const peleaActual = computed(
  () => peleas.value.find((p) => p.pelea_id === peleaActualId.value) || null
);

const proximasPeleas = computed(() =>
  peleas.value
    .filter((p) => {
      if (p.pelea_id === peleaActualId.value) return false;
      if (p.estado === "finalizada") return false;
      return p.rojo?.nombre || p.azul?.nombre;
    })
    .slice(0, 5)
);

const puedeCambiarPelea = computed(() => {
  if (!peleaActual.value) return true;
  return ["pendiente", "finalizada", "tiempo_agotado"].includes(estadoPelea.value);
});

const marcador = ref({
  puntos_rojo: 0,
  puntos_azul: 0,
  ventajas_rojo: 0,
  ventajas_azul: 0,
  amonestaciones_rojo: 0,
  amonestaciones_azul: 0,
});

const DURACION_SEG = ref(300);
const segundosLocal = ref(0);
const cronActivoLocal = ref(false);
let tickInterval = null,
  lastSyncTime = null,
  lastSyncSeg = 0;

const estadoPelea = computed(() => peleaActual.value?.estado || "pendiente");
const esFinalizada = computed(() => estadoPelea.value === "finalizada");
const esEnCurso = computed(() => estadoPelea.value === "en_curso");
const esTiempoAgotado = computed(() => estadoPelea.value === "tiempo_agotado");
const esPausada = computed(() => estadoPelea.value === "pausada");
const esPendiente = computed(() => estadoPelea.value === "pendiente");

const declaracionManual = ref(false);
const mostrarDeclarar = computed(
  () => (esTiempoAgotado.value || declaracionManual.value) && !esFinalizada.value
);

const tiempoDisplay = computed(() => {
  const r = Math.max(0, DURACION_SEG.value - segundosLocal.value);
  return `${String(Math.floor(r / 60)).padStart(2, "0")}:${String(
    Math.floor(r % 60)
  ).padStart(2, "0")}`;
});
const porcentaje = computed(() =>
  Math.min(100, (segundosLocal.value / DURACION_SEG.value) * 100)
);

onMounted(() => {
  if (tatamiId.value) {
    localStorage.setItem("juez_tatami_id", tatamiId.value);
  }
  conectar();
});
onUnmounted(() => {
  detenerTickLocal();
  closeTatami();
});

function conectar() {
  connectTatami({
    tatamiId: tatamiId.value,
    onOpen() {
      wsConectado.value = true;
    },
    onClose() {
      wsConectado.value = false;
      detenerTickLocal();
    },
    onMessage(data) {
      manejarMensaje(data);
    },
  });
}

function manejarMensaje(msg) {
  if (msg.type === "estado_inicial") {
    peleas.value = msg.data?.peleas || [];
    DURACION_SEG.value = msg.data?.duracion_seg || 300;
    const primera = peleas.value.find((p) => p.estado !== "finalizada");
    if (primera) seleccionarPelea(primera.pelea_id, true);
    return;
  }
  if (msg.type === "error") {
    console.warn("[WS]", msg.mensaje);
    return;
  }
  if (msg.type === "marcador_update") {
    const p = peleas.value.find((p) => String(p.pelea_id) === String(msg.pelea_id));
    if (p && msg.estado_pelea) p.estado = msg.estado_pelea;
    if (msg.duracion_seg) DURACION_SEG.value = msg.duracion_seg;
    sincronizarMarcador(msg);
    return;
  }
  const m = msg.marcador || msg;
  if (m?.puntos_rojo !== undefined) sincronizarMarcador(msg);
  if (msg.estado_pelea) actualizarEstadoPelea(msg.pelea_id, msg.estado_pelea);
}

function seleccionarPelea(id, forzar = false) {
  if (!forzar && !puedeCambiarPelea.value) return;
  peleaActualId.value = id;
  declaracionManual.value = false;
  detenerTickLocal();
  const p = peleas.value.find((p) => p.pelea_id === id);
  if (!p) return;
  if (p.duracion_seg) DURACION_SEG.value = p.duracion_seg;
  const m = p.marcador || {};
  marcador.value = {
    puntos_rojo: m.puntos_rojo || 0,
    puntos_azul: m.puntos_azul || 0,
    ventajas_rojo: m.ventajas_rojo || 0,
    ventajas_azul: m.ventajas_azul || 0,
    amonestaciones_rojo: m.amonestaciones_rojo || 0,
    amonestaciones_azul: m.amonestaciones_azul || 0,
  };
  aplicarSyncTiempo(
    m.tiempo_transcurrido || 0,
    m.server_timestamp,
    m.cronometro_activo || false
  );
}

function sincronizarMarcador(msg) {
  const m = msg.marcador || msg;
  if (msg.pelea_id && String(msg.pelea_id) !== String(peleaActualId.value)) {
    const p = peleas.value.find((p) => String(p.pelea_id) === String(msg.pelea_id));
    if (p) p.marcador = m;
    return;
  }
  Object.assign(marcador.value, {
    puntos_rojo: m.puntos_rojo ?? marcador.value.puntos_rojo,
    puntos_azul: m.puntos_azul ?? marcador.value.puntos_azul,
    ventajas_rojo: m.ventajas_rojo ?? marcador.value.ventajas_rojo,
    ventajas_azul: m.ventajas_azul ?? marcador.value.ventajas_azul,
    amonestaciones_rojo: m.amonestaciones_rojo ?? marcador.value.amonestaciones_rojo,
    amonestaciones_azul: m.amonestaciones_azul ?? marcador.value.amonestaciones_azul,
  });
  if (m.tiempo_transcurrido !== undefined)
    aplicarSyncTiempo(
      m.tiempo_transcurrido,
      m.server_timestamp || msg.server_timestamp,
      m.cronometro_activo ?? cronActivoLocal.value
    );
  if (msg.estado_pelea) actualizarEstadoPelea(peleaActualId.value, msg.estado_pelea);
}

function actualizarEstadoPelea(id, estado) {
  const p = peleas.value.find((p) => String(p.pelea_id) === String(id));
  if (p) p.estado = estado;
  if (String(id) === String(peleaActualId.value)) {
    if (["finalizada", "tiempo_agotado"].includes(estado)) {
      detenerTickLocal();
      cronActivoLocal.value = false;
    }
    if (estado === "finalizada") {
      declaracionManual.value = false;
      setTimeout(() => avanzarSiguiente(), 800);
    }
  }
}

function aplicarSyncTiempo(tv, st, activo) {
  let base = tv;
  if (st) {
    const lat = Math.max(0, Math.floor((Date.now() - new Date(st).getTime()) / 1000));
    if (activo) base = tv + lat;
  }
  lastSyncSeg = base;
  lastSyncTime = Date.now();
  segundosLocal.value = Math.min(base, DURACION_SEG.value);
  const era = cronActivoLocal.value;
  cronActivoLocal.value = activo;
  if (activo && !era) iniciarTickLocal();
  if (!activo && era) detenerTickLocal();
}

function iniciarTickLocal() {
  detenerTickLocal();
  tickInterval = setInterval(() => {
    if (!cronActivoLocal.value || !lastSyncTime) return;
    const nuevo = Math.min(
      lastSyncSeg + (Date.now() - lastSyncTime) / 1000,
      DURACION_SEG.value
    );
    segundosLocal.value = nuevo;
    // Cuando el tiempo se agota localmente solo detenemos el tick —
    // Celery Beat detecta el tiempo agotado en el backend y hace el broadcast.
    if (nuevo >= DURACION_SEG.value && cronActivoLocal.value) {
      cronActivoLocal.value = false;
      detenerTickLocal();
    }
  }, 250);
}
function detenerTickLocal() {
  if (tickInterval) {
    clearInterval(tickInterval);
    tickInterval = null;
  }
}

// ── Acciones ──────────────────────────────────────────────────────────────────
function accion(color, tipo, valor, operacion = "sumar") {
  if (!peleaActual.value || esFinalizada.value || esTiempoAgotado.value) return;
  if (!esEnCurso.value) return;
  sendTatami({
    accion: "modificar_marcador",
    pelea_id: peleaActualId.value,
    tipo,
    color,
    valor,
    operacion,
  });
}

function iniciarTiempo() {
  sendTatami({
    accion: "control_tiempo",
    pelea_id: peleaActualId.value,
    operacion: "iniciar",
  });
}
function pausarTiempo() {
  sendTatami({
    accion: "control_tiempo",
    pelea_id: peleaActualId.value,
    operacion: "pausar",
  });
}

function onDeclarar({ color, tipo }) {
  if (!peleaActualId.value) return;
  sendTatami({
    accion: "finalizar_pelea",
    pelea_id: peleaActualId.value,
    ganador: color,
    tipo,
  });
}

function avanzarSiguiente() {
  const sig = peleas.value.find(
    (p) =>
      p.estado !== "finalizada" &&
      p.pelea_id !== peleaActualId.value &&
      (p.rojo?.nombre || p.azul?.nombre)
  );
  if (sig) seleccionarPelea(sig.pelea_id, true);
}

function salir() {
  closeTatami();
  localStorage.removeItem("evento_token");
  localStorage.removeItem("refresh");
  router.push("/evento/login");
}
</script>

<template>
  <div class="app">
    <TatamiHeader
      :tatamiId="tatamiId"
      :wsConectado="wsConectado"
      :peleaActual="peleaActual"
      @salir="salir"
    />

    <div v-if="!peleaActual && wsConectado" class="empty">
      <span>⚔️</span>
      <p>Sin peleas asignadas</p>
    </div>

    <div v-if="peleaActual" class="board">
      <!-- ── Cronómetro ── -->
      <div class="timer-section">
        <div class="timer" :class="{ danger: porcentaje > 85, agotado: esTiempoAgotado }">
          {{ tiempoDisplay }}
        </div>

        <div class="timer-btns" v-if="!esFinalizada && !esTiempoAgotado">
          <button
            v-if="esPendiente || esPausada"
            class="tbtn play"
            @click="iniciarTiempo"
          >
            ▶ {{ esPausada ? "REANUDAR" : "INICIAR" }}
          </button>
          <button v-if="esEnCurso" class="tbtn pause" @click="pausarTiempo">
            ⏸ PAUSAR
          </button>
          <button
            v-if="esPendiente || esPausada"
            class="tbtn end"
            @click="declaracionManual = true"
          >
            🏅 FINALIZAR
          </button>
        </div>

        <div v-if="esTiempoAgotado" class="agotado-tag">
          ⏱ TIEMPO AGOTADO — DECLARA GANADOR
        </div>

        <span class="estado-tag" :class="estadoPelea">
          {{
            {
              pendiente: "Pendiente",
              en_curso: "En curso",
              pausada: "Pausada",
              tiempo_agotado: "T. Agotado",
              finalizada: "Finalizada",
            }[estadoPelea]
          }}
        </span>
      </div>

      <!-- ── Jugador ROJO ── -->
      <div class="player rojo">
        <div class="pinfo">
          <div class="pmeta">
            <div class="pname">{{ peleaActual.rojo?.nombre || "BYE" }}</div>
            <div class="pteam">{{ peleaActual.rojo?.equipo || "—" }}</div>
          </div>
          <div class="ptotal">{{ marcador.puntos_rojo }}</div>
        </div>
        <div class="pgrid" :class="{ blocked: !esEnCurso }">
          <div class="pcell">
            <button class="ab plus" @click="accion('rojo', 'puntos', 2)">+</button>
            <div class="aval">2</div>
            <button class="ab minus" @click="accion('rojo', 'puntos', 1, 'restar')">
              −
            </button>
          </div>
          <div class="pcell">
            <button class="ab plus" @click="accion('rojo', 'puntos', 3)">+</button>
            <div class="aval">3</div>
            <button class="ab minus" @click="accion('rojo', 'puntos', 1, 'restar')">
              −
            </button>
          </div>
          <div class="pcell">
            <button class="ab plus" @click="accion('rojo', 'puntos', 4)">+</button>
            <div class="aval">4</div>
            <button class="ab minus" @click="accion('rojo', 'puntos', 1, 'restar')">
              −
            </button>
          </div>
          <div class="pcell pen" :class="{ hot: marcador.amonestaciones_rojo >= 2 }">
            <button class="ab plus" @click="accion('rojo', 'amonestacion', 1)">+</button>
            <div class="aval red">{{ marcador.amonestaciones_rojo }}</div>
            <button class="ab minus" @click="accion('rojo', 'amonestacion', 1, 'restar')">
              −
            </button>
          </div>
          <div class="pcell adv">
            <button class="ab plus" @click="accion('rojo', 'ventaja', 1)">+</button>
            <div class="aval ylw">{{ marcador.ventajas_rojo }}</div>
            <button class="ab minus" @click="accion('rojo', 'ventaja', 1, 'restar')">
              −
            </button>
          </div>
        </div>
      </div>

      <!-- ── Jugador AZUL ── -->
      <div class="player azul">
        <div class="pinfo">
          <div class="pmeta">
            <div class="pname">{{ peleaActual.azul?.nombre || "BYE" }}</div>
            <div class="pteam">{{ peleaActual.azul?.equipo || "—" }}</div>
          </div>
          <div class="ptotal">{{ marcador.puntos_azul }}</div>
        </div>
        <div class="pgrid" :class="{ blocked: !esEnCurso }">
          <div class="pcell">
            <button class="ab plus" @click="accion('azul', 'puntos', 2)">+</button>
            <div class="aval">2</div>
            <button class="ab minus" @click="accion('azul', 'puntos', 1, 'restar')">
              −
            </button>
          </div>
          <div class="pcell">
            <button class="ab plus" @click="accion('azul', 'puntos', 3)">+</button>
            <div class="aval">3</div>
            <button class="ab minus" @click="accion('azul', 'puntos', 1, 'restar')">
              −
            </button>
          </div>
          <div class="pcell">
            <button class="ab plus" @click="accion('azul', 'puntos', 4)">+</button>
            <div class="aval">4</div>
            <button class="ab minus" @click="accion('azul', 'puntos', 1, 'restar')">
              −
            </button>
          </div>
          <div class="pcell pen" :class="{ hot: marcador.amonestaciones_azul >= 2 }">
            <button class="ab plus" @click="accion('azul', 'amonestacion', 1)">+</button>
            <div class="aval red">{{ marcador.amonestaciones_azul }}</div>
            <button class="ab minus" @click="accion('azul', 'amonestacion', 1, 'restar')">
              −
            </button>
          </div>
          <div class="pcell adv">
            <button class="ab plus" @click="accion('azul', 'ventaja', 1)">+</button>
            <div class="aval ylw">{{ marcador.ventajas_azul }}</div>
            <button class="ab minus" @click="accion('azul', 'ventaja', 1, 'restar')">
              −
            </button>
          </div>
        </div>
      </div>

      <Transition name="slide-up">
        <DeclaraGanador
          v-if="mostrarDeclarar"
          :peleaActual="peleaActual"
          :esTiempoAgotado="esTiempoAgotado"
          @declarar="onDeclarar"
          @cancelar="declaracionManual = false"
        />
      </Transition>

      <ProximasPeleas
        :peleas="proximasPeleas"
        :bloqueado="!puedeCambiarPelea"
        @seleccionar="(id) => seleccionarPelea(id)"
      />
    </div>
  </div>
</template>

<style scoped>
*,
*::before,
*::after {
  box-sizing: border-box;
}

.app {
  min-height: 100vh;
  background: linear-gradient(rgba(20, 30, 40, 0.5), rgba(20, 30, 40, 0.6)),
    url("@/assets/img/Evolve/JiuJitsu_Background2.png");
  background-size: cover;
  background-position: center;
  background-attachment: fixed;
  display: flex;
  flex-direction: column;
  font-family: "Inter", system-ui, sans-serif;
  color: #000000;
  user-select: none;
  -webkit-tap-highlight-color: transparent;
}

.empty {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  color: #9ca3af;
}
.empty span {
  font-size: 40px;
}

.board {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px 12px;
}

/* ── Timer section ── */
.timer-section {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 14px;
  background: #ffffff;
  border-radius: 12px;
  padding: 12px 20px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
}
.timer {
  font-size: clamp(2.4rem, 8vw, 4rem);
  font-weight: 900;
  letter-spacing: 4px;
  font-variant-numeric: tabular-nums;
  color: #000000;
  min-width: 140px;
  text-align: center;
  transition: color 0.3s;
}
.timer.danger {
  color: #dc2626;
}
.timer.agotado {
  color: #dc2626;
  animation: tblink 0.9s infinite;
}
@keyframes tblink {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.3;
  }
}

.timer-btns {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}
.tbtn {
  padding: 9px 18px;
  border: none;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.5px;
  cursor: pointer;
  transition: filter 0.15s, transform 0.1s;
}
.tbtn:active {
  transform: scale(0.95);
}
.tbtn.play {
  background: #16a34a;
  color: #fff;
}
.tbtn.pause {
  background: #d97706;
  color: #fff;
}
.tbtn.end {
  background: #f3f4f6;
  color: #374151;
  border: 1px solid #d1d5db;
}
.tbtn:hover {
  filter: brightness(1.08);
}

.agotado-tag {
  font-size: 12px;
  font-weight: 700;
  color: #dc2626;
  letter-spacing: 0.8px;
  text-transform: uppercase;
}

.estado-tag {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  padding: 4px 12px;
  border-radius: 20px;
  background: #e5e7eb;
  color: #6b7280;
}
.estado-tag.en_curso {
  background: #dcfce7;
  color: #15803d;
}
.estado-tag.pausada {
  background: #fef3c7;
  color: #b45309;
}
.estado-tag.tiempo_agotado {
  background: #fee2e2;
  color: #dc2626;
}
.estado-tag.finalizada {
  background: #f3f4f6;
  color: #9ca3af;
}

/* ── Player row ── */
.player {
  display: flex;
  align-items: stretch;
  background: #ffffff;
  border-radius: 12px;
  overflow: hidden;
  border-left: 6px solid transparent;
  min-height: 115px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.07);
}
.player.rojo {
  border-left-color: #dc2626;
}
.player.azul {
  border-left-color: #2563eb;
}

.pinfo {
  width: 250px;
  flex-shrink: 0;
  padding: 12px 14px;
  background: #f9fafb;
  border-right: 1px solid #e5e7eb;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 2px;
}
.pmeta {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}
.pname {
  font-size: clamp(13px, 1.6vw, 15px);
  font-weight: 800;
  text-transform: uppercase;
  line-height: 1.2;
  color: #000000;
  word-break: break-word;
  white-space: normal;
}
.pteam {
  font-size: 10px;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-top: 1px;
}
.ptotal {
  font-size: clamp(2.2rem, 5vw, 3.4rem);
  font-weight: 900;
  line-height: 1;
  margin-top: 6px;
}
.rojo .ptotal {
  color: #dc2626;
}
.azul .ptotal {
  color: #2563eb;
}

/* ── Actions grid ── */
.pgrid {
  flex: 1;
  display: flex;
  align-items: stretch;
  transition: opacity 0.2s;
}
.pgrid.blocked {
  opacity: 0.22;
  pointer-events: none;
}
.pcell {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  padding: 8px 6px;
  gap: 4px;
  border-right: 1px solid #e5e7eb;
}
.pcell:last-child {
  border-right: none;
}
.pcell.pen {
  background: #f8cdcd;
}
.pcell.adv {
  background: #fff2c0;
}
.pcell.pen.hot {
  background: #fee2e2;
  animation: phot 0.8s infinite;
}
@keyframes phot {
  0%,
  100% {
    background: #fee2e2;
  }
  50% {
    background: #fff5f5;
  }
}

.ab {
  width: 100%;
  padding: 8px 0;
  background: #f3f4f6;
  border: 1px solid #d1d5db;
  border-radius: 7px;
  color: #374151;
  font-size: 18px;
  font-weight: 700;
  cursor: pointer;
  line-height: 1;
  transition: background 0.12s, transform 0.1s;
  touch-action: manipulation;
}
.ab:active {
  transform: scale(0.91);
  background: #e5e7eb;
}
.ab.plus {
  background: #e0f2fe;
  border-color: #bae6fd;
  color: #0369a1;
}
.ab.plus:active {
  background: #bae6fd;
}
.ab:hover {
  filter: brightness(0.96);
}

.alabel {
  font-size: 9px;
  font-weight: 700;
  text-transform: uppercase;
  color: #6b7280;
  text-align: center;
  line-height: 1.35;
}
.alabel em {
  font-style: normal;
  font-size: 8px;
  color: #9ca3af;
}

.aval {
  font-size: clamp(1.2rem, 2.5vw, 1.8rem);
  font-weight: 900;
  color: #000000;
  line-height: 1;
}
.aval.red {
  color: #dc2626;
}
.aval.ylw {
  color: #d97706;
}

/* ── Transitions ── */
.slide-up-enter-active {
  transition: all 0.2s ease-out;
}
.slide-up-leave-active {
  transition: all 0.15s ease-in;
}
.slide-up-enter-from,
.slide-up-leave-to {
  opacity: 0;
  transform: translateY(8px);
}

/* ── Responsive ── */
@media (max-width: 640px) {
  .player {
    flex-direction: column;
    min-height: auto;
  }
  .pinfo {
    width: 100%;
    border-right: none;
    border-bottom: 1px solid #e5e7eb;
    padding: 10px 12px;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
  }
  .pmeta {
    flex: 1;
    min-width: 0;
  }
  .pname {
    font-size: 13px;
    line-height: 1.15;
  }
  .pteam {
    font-size: 10px;
    margin-top: 2px;
  }
  .ptotal {
    margin-top: 0;
    flex-shrink: 0;
    font-size: 2.4rem;
    min-width: 42px;
    text-align: center;
  }
  .pgrid {
    width: 100%;
  }
  .pcell {
    padding: 8px 4px;
  }
  .alabel {
    font-size: 8px;
  }
  .ab {
    font-size: 16px;
    padding: 7px 0;
  }
}

@media (max-width: 420px) {
  .pinfo {
    padding: 8px 10px;
  }
  .pname {
    font-size: 12px;
  }
  .pteam {
    font-size: 9px;
  }
  .ptotal {
    font-size: 2.1rem;
    min-width: 36px;
  }
  .alabel em {
    display: none;
  }
  .timer {
    font-size: 2rem;
  }
}
</style>
